import { useCallback, useEffect, useState } from 'react';
import { useStableElementPosition } from '../../../hooks/use-stable-element-position';
import FeatureGates from '@atlaskit/feature-gate-js-client';
import {
	type BackendPlacementMessage,
	defaultMessageBackend,
	type FrontendPlacementMessage,
	type FrontendStage,
	noValidation,
} from '@post-office/placement-common-v2';
import {
	PlacementBackend,
	PlacementFrontend,
	type PlacementRenderProps,
} from '@post-office/placements--alexstrasza/operations-overview-discoverability-spotlights';
import { useMountToHtmlElement } from '@post-office/components--post-office-team';
import { useFirePostOfficeAnalyticsEvents } from '@post-office/analytics';
import { useMessageInstanceEvent } from '@post-office/message-instance-event-frontend-client';
import { useMessageEvent } from '@post-office/message-lifecycle';

import {
	marketplaceOpsMessages,
	inactiveOpsMessages,
	marketplaceOpsTourMessages,
	inactiveOpsTourMessages,
	highBugLoadMessages,
	highBugLoadTourMessages,
} from '../../../components/messages';
import { OperationsMenuItemSpotlight } from '../../../components/operations-menu-item-spotlight';
import { OperationsDiscoverySpotlightTour } from '../../../components/operations-discovery-spotlight-tour';
import { type MessageTemplateContext } from '../../../index';
import {
	type CreatePlacementMessageConfig,
	type BackendSyncMessageStage,
	type AnyPayload,
	type AnyBackendMessageStage,
} from '@post-office/placement-common-v2/create-placement/types';
import { type TransformMetadataResult } from '@post-office/shared-contracts/message-template-metadata';

export const MessageBackend: (config: CreatePlacementMessageConfig) => BackendPlacementMessage<{
	transformMetadata: BackendSyncMessageStage<AnyPayload, TransformMetadataResult>;
	hydrate?: AnyBackendMessageStage;
}> = PlacementBackend.createMessageBackend(defaultMessageBackend);

type RenderProps = MessageTemplateContext & PlacementRenderProps;

// The original experiments will be paused via Switcheroo once the new April experiments below are enabled.
const MARKETPLACE_OPS_EXPERIMENT_KEY = 'ops-overview-disc-marketplace-ops';
const INACTIVE_OPS_EXPERIMENT_KEY = 'ops-overview-disc-inactive-ops';

// New April experiments with corrected segment targeting.
const MARKETPLACE_OPS_APRIL_EXPERIMENT_KEY = 'ops-overview-disc-marketplace-ops-april';
const INACTIVE_OPS_APRIL_EXPERIMENT_KEY = 'ops-overview-disc-inactive-ops-april';
const HIGH_BUG_LOAD_EXPERIMENT_KEY = 'ops-overview-disc-high-bug';

type ActiveExperiment = {
	experimentKey: string;
	messages: typeof marketplaceOpsMessages;
	tourMessages: typeof marketplaceOpsTourMessages;
};

const getActiveExperiment = (): ActiveExperiment | null => {
	// Priority: Marketplace > Inactive Ops > High Bug Load.
	const isMarketplaceOpsAprilEnabled = FeatureGates.getExperimentValue<boolean>(
		MARKETPLACE_OPS_APRIL_EXPERIMENT_KEY,
		'isEnabled',
		false,
	);

	if (isMarketplaceOpsAprilEnabled) {
		return {
			experimentKey: MARKETPLACE_OPS_APRIL_EXPERIMENT_KEY,
			messages: marketplaceOpsMessages,
			tourMessages: marketplaceOpsTourMessages,
		};
	}

	const isInactiveOpsAprilEnabled = FeatureGates.getExperimentValue<boolean>(
		INACTIVE_OPS_APRIL_EXPERIMENT_KEY,
		'isEnabled',
		false,
	);

	if (isInactiveOpsAprilEnabled) {
		return {
			experimentKey: INACTIVE_OPS_APRIL_EXPERIMENT_KEY,
			messages: inactiveOpsMessages,
			tourMessages: inactiveOpsTourMessages,
		};
	}

	const isHighBugLoadEnabled = FeatureGates.getExperimentValue<boolean>(
		HIGH_BUG_LOAD_EXPERIMENT_KEY,
		'isEnabled',
		false,
	);

	if (isHighBugLoadEnabled) {
		return {
			experimentKey: HIGH_BUG_LOAD_EXPERIMENT_KEY,
			messages: highBugLoadMessages,
			tourMessages: highBugLoadTourMessages,
		};
	}

	// Segments were mis-targeted, so messages are intentionally swapped.
	const isInactiveOpsEnabled = FeatureGates.getExperimentValue<boolean>(
		INACTIVE_OPS_EXPERIMENT_KEY,
		'isEnabled',
		false,
	);

	if (isInactiveOpsEnabled) {
		return {
			experimentKey: INACTIVE_OPS_EXPERIMENT_KEY,
			messages: marketplaceOpsMessages,
			tourMessages: marketplaceOpsTourMessages,
		};
	}

	const isMarketplaceOpsEnabled = FeatureGates.getExperimentValue<boolean>(
		MARKETPLACE_OPS_EXPERIMENT_KEY,
		'isEnabled',
		false,
	);

	if (isMarketplaceOpsEnabled) {
		return {
			experimentKey: MARKETPLACE_OPS_EXPERIMENT_KEY,
			messages: inactiveOpsMessages,
			tourMessages: inactiveOpsTourMessages,
		};
	}

	return null;
};

const isOperationsMenuItemVisible = (): boolean => {
	return !!globalThis.document?.querySelector(
		'#operations-overview-discoverability-spotlight-target',
	);
};

export const render: {
	type: 'render';
	validate: (input: unknown) => RenderProps;
	Component: (input: PlacementRenderProps & Record<string, never>) => JSX.Element | null;
} = PlacementFrontend.createFrontendStage({
	type: 'render',
	validate: noValidation<RenderProps>(),
	Component: ({ onDismiss, onClick }: RenderProps) => {
		const { messageDismissed } = useMessageEvent();
		const { messageInstanceClicked, messageInstanceViewed } = useMessageInstanceEvent();
		const { fireAnalyticsEvent } = useFirePostOfficeAnalyticsEvents();

		const activeExperiment = getActiveExperiment();
		const operationsVisible = isOperationsMenuItemVisible();
		const spotlightType = operationsVisible ? 'single-step' : 'multi-step';
		const [tourStepIndex, setTourStepIndex] = useState(0);
		const selector = operationsVisible
			? '#operations-overview-discoverability-spotlight-target'
			: '#more-button-menu-item';

		const { isStable } = useStableElementPosition(selector);

		const renderInline = useMountToHtmlElement({
			selectors: selector,
			as: 'inline',
			position: 'after',
		});

		// Fire viewed event when spotlight is seen by the user
		useEffect(() => {
			if (!messageInstanceViewed || !isStable || !activeExperiment) {
				return;
			}

			void messageInstanceViewed({
				experimentKey: activeExperiment.experimentKey,
				spotlightType,
			});
			fireAnalyticsEvent({
				eventType: 'screen',
				name: 'operationsDiscoverabilitySpotlight',
				attributes: {
					experimentKey: activeExperiment.experimentKey,
					spotlightType,
				},
			});
			// eslint-disable-next-line react-hooks/exhaustive-deps
		}, [isStable]);

		const dismiss = useCallback(() => {
			void messageDismissed();
			void messageInstanceClicked({
				messageEventType: 'clicked',
				clickedElement: {
					clickIdentifier: 'spotlight-dismissed',
					clickedElement: 'button',
					buttonType: 'secondary',
				},
				action: { actionType: 'dismiss' },
				additionalMetadataFromFrontend: {
					experimentKey: activeExperiment?.experimentKey ?? '',
					spotlightType,
					tourStep: tourStepIndex,
				},
			});
			fireAnalyticsEvent({
				eventType: 'ui',
				action: 'clicked',
				actionSubject: 'button',
				actionSubjectId: 'operationsDiscoverabilitySpotlightDismiss',
				attributes: {
					experimentKey: activeExperiment?.experimentKey ?? '',
					spotlightType,
					tourStep: tourStepIndex,
				},
			});
			onDismiss?.();
		}, [
			messageDismissed,
			messageInstanceClicked,
			fireAnalyticsEvent,
			onDismiss,
			activeExperiment,
			spotlightType,
			tourStepIndex,
		]);

		const handlePrimaryAction = useCallback(() => {
			void messageDismissed();
			void messageInstanceClicked({
				messageEventType: 'clicked',
				clickedElement: {
					clickIdentifier: 'go-to-operations-overview',
					clickedElement: 'button',
					buttonType: 'primary',
				},
				action: { actionType: 'dismiss' },
				additionalMetadataFromFrontend: {
					experimentKey: activeExperiment?.experimentKey ?? '',
					spotlightType,
				},
			});
			fireAnalyticsEvent({
				eventType: 'ui',
				action: 'clicked',
				actionSubject: 'button',
				actionSubjectId: 'operationsDiscoverabilitySpotlightGoToOperations',
				attributes: {
					experimentKey: activeExperiment?.experimentKey ?? '',
					spotlightType,
				},
			});
			onClick?.();
			onDismiss?.();
		}, [
			messageDismissed,
			messageInstanceClicked,
			fireAnalyticsEvent,
			onClick,
			onDismiss,
			activeExperiment,
			spotlightType,
		]);

		const handleTourNext = useCallback(() => {
			// Click the More button to open the menu
			const moreButton = globalThis.document?.querySelector<HTMLElement>(
				'#more-button-menu-item button',
			);
			moreButton?.click();
			void messageInstanceClicked({
				messageEventType: 'clicked',
				clickedElement: {
					clickIdentifier: 'tour-next-clicked',
					clickedElement: 'button',
					buttonType: 'primary',
				},
				action: { actionType: 'dismiss' },
				additionalMetadataFromFrontend: {
					experimentKey: activeExperiment?.experimentKey ?? '',
					spotlightType,
					tourStep: 0,
				},
			});
			fireAnalyticsEvent({
				eventType: 'ui',
				action: 'clicked',
				actionSubject: 'button',
				actionSubjectId: 'operationsDiscoverabilitySpotlightTourNext',
				attributes: {
					experimentKey: activeExperiment?.experimentKey ?? '',
					spotlightType,
				},
			});
			// Delay step advance to allow More menu popup to render
			setTimeout(() => {
				setTourStepIndex(1);
			}, 300);
		}, [fireAnalyticsEvent, messageInstanceClicked, activeExperiment, spotlightType]);

		if (!activeExperiment || !isStable) {
			return null;
		}

		// Operations menu item is visible — show single-step spotlight
		if (operationsVisible) {
			const { messages } = activeExperiment;

			return (
				<div>
					{renderInline(
						<OperationsMenuItemSpotlight
							dismiss={dismiss}
							onPrimaryAction={handlePrimaryAction}
							messages={messages}
						/>,
					)}
				</div>
			);
		}

		// Operations menu item is NOT visible — show multi-step tour
		const { tourMessages } = activeExperiment;

		return (
			<div>
				{renderInline(
					<OperationsDiscoverySpotlightTour
						dismiss={dismiss}
						next={handleTourNext}
						done={handlePrimaryAction}
						stepIndex={tourStepIndex}
						totalSteps={2}
						experimentMessages={tourMessages}
					/>,
				)}
			</div>
		);
	},
});

export const MessageFrontend: (config: CreatePlacementMessageConfig) => FrontendPlacementMessage<{
	render: FrontendStage<PlacementRenderProps>;
}> = PlacementFrontend.createMessageFrontend({
	render,
});
